import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useReportStore } from '../../../stores/useReportStore';
import CalculatorSlider from '../widgets/CalculatorSlider';
import Section from '../widgets/Section';
import MultiLine from '../charts/MultiLine';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatMoney } from '../../../utils/formatMoney';

// Parse "$3B", "$600M", "$12M" etc. into raw numbers
function parseMoneyLabel(s: string): number {
  const m = s.match(/\$?([\d,.]+)\s*(T|B|M|K)?/i);
  if (!m) return 0;
  const num = parseFloat(m[1].replace(/,/g, ''));
  const suffix = (m[2] || '').toUpperCase();
  const multipliers: Record<string, number> = { T: 1e12, B: 1e9, M: 1e6, K: 1e3 };
  return num * (multipliers[suffix] || 1);
}

// Normalize market tier: API returns rich data with description, sources, estimates
function normalizeTier(tier: Record<string, unknown>, fallbackTitle: string, badge: string) {
  const sources = (tier.sources as string[]) || [];
  const estimate = String(tier.estimate || tier.formatted || tier.label || '');
  const rawValue = Number(tier.value || 0);
  // If value is 0 or NaN, try to parse from the label string
  const value = (rawValue && !isNaN(rawValue)) ? rawValue : parseMoneyLabel(estimate);
  return {
    title: String(tier.title || fallbackTitle),
    desc: String(tier.description || tier.desc || ''),
    label: estimate,
    value,
    badge,
    sources,
    // SOM-specific fields
    current: tier.current ? String(tier.current) : undefined,
    target12m: tier['12_month_target_optimized'] ? String(tier['12_month_target_optimized']) : undefined,
    target3y: tier['3_year_target'] ? String(tier['3_year_target']) : undefined,
    penetrationPath: tier.penetration_path ? String(tier.penetration_path) : undefined,
  };
}

// Money formatter: $0 / $2.6K / $241K / $1.2M (one decimal for sub-$10K so a
// small real spend like $2,552 isn't rounded away to "$3K"). Currency comes
// from the report payload (data._currency); absent -> historical "$".
function fmtMoney(n: number, currency?: string | null): string {
  return formatMoney(n, currency, { subTenKDecimal: true });
}

// Real-data-or-null: spend and revenue come from source data, so they are kept
// as `number | null` (null → render a dash). 0 is a REAL value (e.g. $0 ad
// revenue) and must NOT be treated as missing. Only conversionRate/arpu keep
// soft defaults since they drive slider ranges.
type Baseline = {
  monthlySignups: number;
  conversionRate: number | null;
  arpu: number | null;
  monthlyRevenue: number | null;
  monthlySpend: number | null;
  avgCustomerValue: number | null;
};
function normalizeBaseline(val: unknown): Baseline {
  const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  if (!val || typeof val !== 'object') {
    return { monthlySignups: 1560, conversionRate: null, arpu: null, monthlyRevenue: null, monthlySpend: null, avgCustomerValue: null };
  }
  const obj = val as Record<string, unknown>;
  return {
    monthlySignups: Number(obj.monthlySignups || obj.monthly_signups || 1560),
    // conversionRate / arpu are revenue-side metrics (need RevenueCat); kept as
    // number | null so a missing/stripped value renders a dash, never a benchmark.
    conversionRate: num(obj.conversionRate ?? obj.conversion_rate),
    arpu: num(obj.arpu),
    monthlyRevenue: num(obj.monthlyRevenue ?? obj.monthly_revenue),
    monthlySpend: num(obj.monthlySpend ?? obj.monthly_spend),
    // E-commerce-only TAM Calculator anchor (AOV × purchase frequency); null
    // for mobile_app / when no Shopify data was found.
    avgCustomerValue: num(obj.avgCustomerValue ?? obj.avg_customer_value),
  };
}

/** KPI-shaped card that flashes to the brand secondary when its value changes. */
function FlashCard({ value, label, children }: { value: string; label: string; children?: ReactNode }) {
  const [flashing, setFlashing] = useState(false);
  const prevValue = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (prevValue.current !== value) {
      prevValue.current = value;
      setFlashing(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setFlashing(false), 600);
    }
    return () => clearTimeout(timerRef.current);
  }, [value]);

  return (
    <div
      className={cn(
        'rounded-xl border border-border px-4 py-3.5 transition-colors duration-500',
        flashing
          ? 'border-secondary bg-secondary text-secondary-foreground [&_.text-muted-foreground]:text-secondary-foreground/70 [&_.text-success]:text-secondary-foreground [&_.text-destructive]:text-secondary-foreground [&_.bg-secondary]:bg-white [&_.bg-muted]:bg-secondary-foreground/15'
          : 'bg-card',
      )}
    >
      <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 text-[24px] font-bold leading-none tracking-[-0.025em] tabular-nums">{value}</div>
      {children}
    </div>
  );
}

/** Prototype `.mkr__tag`: TAM outlined, SAM ink, SOM report-blue. */
const TIER_TAGS: Record<string, string> = {
  tam: 'border border-border bg-card text-muted-foreground',
  sam: 'bg-primary text-primary-foreground',
  som: 'bg-report-blue text-white',
};

export default function MarketSizing() {
  const data = useReportStore((s) => s.reportData);

  const [budgetMult, setBudgetMult] = useState(1.0);
  // Absolute values — initialized from baseline after data loads
  const [convRate, setConvRate] = useState<number | null>(null);
  const [arpu, setArpu] = useState<number | null>(null);

  // TAM Calculator — initialized lazily from report data
  const [tamUsers, setTamUsers] = useState<number | null>(null);
  const [tamArpu, setTamArpu] = useState<number | null>(null);
  const [tamServiceable, setTamServiceable] = useState<number | null>(null);
  const [tamObtainable, setTamObtainable] = useState<number | null>(null);
  const tamInitialized = useRef(false);

  if (!data) return null;

  const calcBaseline = normalizeBaseline(data.calcBaseline);
  const ms = (data.marketSizing || {}) as unknown as Record<string, unknown>;
  const tam = normalizeTier((ms.tam || {}) as Record<string, unknown>, 'Total Addressable Market', 'tam');
  const sam = normalizeTier((ms.sam || {}) as Record<string, unknown>, 'Serviceable Addressable Market', 'sam');
  const som = normalizeTier((ms.som || {}) as Record<string, unknown>, 'Serviceable Obtainable Market', 'som');

  // A store has no per-user recurring revenue ("users") — the bottom-up
  // model instead splits TAM into addressable Customers × Avg Customer Value.
  const isEcommerce = data._vertical === 'ecommerce' || data._vertical === 'generic';
  // B2B (LinkedIn lead gen) shares e-commerce's shape here: the TAM anchor is
  // avg deal size rather than per-user ARPU, and the addressable-entity count
  // is thousands, not millions — rounding it to whole millions floors it to 0.
  const isB2B = data._vertical === 'b2b';
  const isSmallCount = isEcommerce || isB2B;

  // Derive TAM calculator defaults from report data (once)
  const defaultServiceable = tam.value > 0 ? Math.round((sam.value / tam.value) * 1000) / 10 : 8;
  const defaultObtainable = sam.value > 0 ? Math.round((som.value / sam.value) * 1000) / 10 : 3.5;
  // Split TAM into entities × per-entity annual value using a real baseline
  // as anchor: subscription ARPU (× 12 → annual) for mobile_app, e-commerce's
  // avgCustomerValue (already annual: AOV × purchase frequency) otherwise.
  const defaultArpu = isSmallCount
    ? (calcBaseline.avgCustomerValue != null && calcBaseline.avgCustomerValue > 0 ? calcBaseline.avgCustomerValue : 96)
    : (calcBaseline.arpu != null && calcBaseline.arpu > 0 ? calcBaseline.arpu * 12 : 96);
  // A mass-market SaaS TAM (hundreds of millions of users) needs "M" as the
  // count unit; an e-commerce store's addressable-customer count is orders
  // of magnitude smaller (thousands, not millions) — rounding that to whole
  // millions floors it to 0 and cascades into the "-100%" bug this replaces.
  const tamUnit = isSmallCount ? 1e3 : 1e6;
  const tamUnitLabel = isSmallCount ? 'K' : 'M';
  const defaultUsers = tam.value > 0 ? Math.round(tam.value / defaultArpu / tamUnit) : (isSmallCount ? 16 : 340);

  if (!tamInitialized.current) {
    tamInitialized.current = true;
    if (tamUsers === null) setTamUsers(defaultUsers);
    if (tamArpu === null) setTamArpu(defaultArpu);
    if (tamServiceable === null) setTamServiceable(defaultServiceable);
    if (tamObtainable === null) setTamObtainable(defaultObtainable);
  }

  // Active values (fall back to defaults until state is set)
  const activeTamUsers = tamUsers ?? defaultUsers;
  const activeTamArpu = tamArpu ?? defaultArpu;
  const activeTamServiceable = tamServiceable ?? defaultServiceable;
  const activeTamObtainable = tamObtainable ?? defaultObtainable;

  // Slider ranges: ×0.5 to ×2.0 around anchor
  const tamUsersMin = Math.round(defaultUsers * 0.5);
  const tamUsersMax = Math.round(defaultUsers * 2);
  const tamArpuMin = Math.round(defaultArpu * 0.5);
  const tamArpuMax = Math.round(defaultArpu * 2);

  const penetration = ms.penetration as Record<string, unknown> | undefined;
  const apiPenetration = Number(penetration?.penetration_pct ?? ms.currentPenetration ?? 0);
  const apiRevenue = String(penetration?.current_revenue ?? ms.currentRevenue ?? '');

  // Conversion rate & ARPU — REAL (RevenueCat-derived) or null → dash. No
  // hardcoded benchmark fallback.
  const cr = calcBaseline.conversionRate; // number | null
  const ar = calcBaseline.arpu;            // number | null
  const hasConv = cr != null;
  const hasArpu = ar != null;
  const activeConvRate = convRate ?? cr ?? 0;
  const activeArpu = arpu ?? ar ?? 0;

  // Slider ranges: -50% to +100% of baseline (meaningful only when real)
  const convMin = cr != null ? Math.round(cr * 0.5 * 10) / 10 : 0;
  const convMax = cr != null ? Math.round(cr * 2 * 10) / 10 : 0;
  const arpuMin = ar != null ? Math.round(ar * 0.5) : 0;
  const arpuMax = ar != null ? Math.round(ar * 2) : 0;

  // Baseline spend & revenue — REAL source data only (null → dash, never a
  // hardcoded fallback or revenue-derived estimate). 0 is a real value.
  const realSpend = calcBaseline.monthlySpend;     // number | null
  const realRevenue = calcBaseline.monthlyRevenue; // number | null
  const hasSpend = realSpend != null;
  const hasRevenue = realRevenue != null;
  const S0 = hasSpend ? realSpend : 0;
  const R0 = hasRevenue ? realRevenue : 0;
  // The revenue projection (Hill curve / ROAS / Efficiency & Scale indexes)
  // needs a real positive spend + revenue anchor AND real ARPU + conversion
  // (all revenue-side — they require RevenueCat). Without them we GATE the
  // projection rather than model off fabricated numbers.
  const projectable = hasSpend && S0 > 0 && hasRevenue && R0 > 0 && hasConv && hasArpu;

  // Market Penetration = Current Revenue / SOM * 100%
  const currentPenetration = apiPenetration || (som.value > 0 ? parseFloat(((R0 * 12 / som.value) * 100).toFixed(1)) : 0);
  const fmtRevenue = (n: number) => formatMoney(n, data._currency);
  const currentRevenue = apiRevenue && apiRevenue !== '' && apiRevenue !== '$0' ? apiRevenue : fmtRevenue(R0 * 12);

  // Hill function params (used in calc and chart). Guarded so a $0/missing
  // baseline can't produce a divide-by-zero / NaN.
  const L = projectable ? R0 * 3 : 0;
  const k = projectable ? S0 * (L - R0) / R0 : 1;
  const convMod = cr != null && cr !== 0 ? activeConvRate / cr : 1;
  const arpuMod = ar != null && ar !== 0 ? activeArpu / ar : 1;

  const calc = useMemo(() => {
    const newSpend = Math.round(S0 * budgetMult);
    // Revenue from Hill curve (same model as chart), adjusted by conv & arpu sliders.
    // Without a projectable baseline, revenue stays at the real R0 (e.g. $0).
    const baseRevenue = projectable ? L * (newSpend / (newSpend + k)) : R0;
    const newRevenue = Math.round(baseRevenue * convMod * arpuMod);

    const spendDelta = S0 ? ((newSpend / S0 - 1) * 100).toFixed(0) : '0';
    const revDelta = R0 ? ((newRevenue / R0 - 1) * 100).toFixed(0) : '0';

    // Efficiency Index = (projected revenue / spend) / (baseline revenue / baseline spend) * 100
    const baselineEfficiency = S0 > 0 ? R0 / S0 : 0;
    const currentEfficiency = newSpend > 0 ? newRevenue / newSpend : 0;
    const efficiencyPct = baselineEfficiency > 0 ? Math.round((currentEfficiency / baselineEfficiency) * 100) : 100;
    const effDelta = (efficiencyPct - 100).toFixed(0);

    // Scale Index = (revenue growth / spend increase) * Market Saturation Factor
    // Market Saturation Factor = baseline revenue / SOM * 100%
    const revenueGrowthRatio = R0 > 0 ? newRevenue / R0 : 1;
    const spendIncreaseRatio = budgetMult;
    const somVal = som.value || 1;
    const marketSaturation = (R0 / somVal) * 100;
    const scaleIndex = Math.round((revenueGrowthRatio / spendIncreaseRatio) * marketSaturation);
    const baselineScaleIndex = Math.round(marketSaturation); // at 1x: revenueGrowth=1, spendIncrease=1
    const scaleDelta = baselineScaleIndex > 0 ? ((scaleIndex / baselineScaleIndex - 1) * 100).toFixed(0) : '0';

    return { newSpend, newRevenue, efficiencyPct, scaleIndex, spendDelta, effDelta, revDelta, scaleDelta };
  }, [budgetMult, convMod, arpuMod, S0, R0, L, k, som.value, projectable]);

  // ── Chart: Spend / Revenue / ROAS vs Budget Multiplier ──
  const multipliers = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0];
  const chartPoints = useMemo(() => multipliers.map((m) => {
    const spend = S0 * m;
    const baseRevenue = L * (spend / (spend + k));
    const revenue = baseRevenue * convMod * arpuMod;
    const roas = spend > 0 ? revenue / spend : 0;
    return { m, spend, revenue, roas };
  }), [S0, L, k, convMod, arpuMod]);

  // Baseline ROAS for efficiency index
  const baselineRoas = S0 > 0 ? R0 / S0 : 0;
  const currentRoas = chartPoints.find((p) => p.m === budgetMult)?.roas
    ?? (S0 * budgetMult > 0 ? (L * (S0 * budgetMult / (S0 * budgetMult + k)) * convMod * arpuMod) / (S0 * budgetMult) : 0);
  const efficiencyIndex = baselineRoas > 0 ? Math.round((currentRoas / baselineRoas) * 100) : 100;

  const getDeltaColor = (n: string) => Number(n) > 0 ? 'text-success' : Number(n) < 0 ? 'text-destructive' : 'text-muted-foreground';
  const getDeltaPrefix = (n: string) => Number(n) > 0 ? '+' : '';

  const tiers = [tam, sam, som];

  return (
    <div>
      {/* Growth Scenario Simulator */}
      <Section
        title="Growth scenario simulator"
        hint="Adjust the levers to model growth scenarios and see projected outcomes."
      >
        <Card>
          <CardContent className="px-5.5 py-1">
            {projectable ? (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                  <CalculatorSlider label="Budget Multiplier" value={budgetMult} min={0.5} max={5} step={0.1} displayValue={`${budgetMult.toFixed(1)}x`} onChange={setBudgetMult} minLabel="0.5x" maxLabel="5x" defaultLabel="1.0x" />
                  <CalculatorSlider label="Conversion Rate" value={activeConvRate} min={convMin} max={convMax} step={0.1} displayValue={`${activeConvRate.toFixed(1)}%`} onChange={setConvRate} minLabel="-50%" maxLabel="+100%" defaultLabel={`${cr}%`} />
                  <CalculatorSlider label="ARPU" value={activeArpu} min={arpuMin} max={arpuMax} step={1} displayValue={`$${activeArpu.toFixed(0)}`} onChange={setArpu} minLabel="-50%" maxLabel="+100%" defaultLabel={`$${ar}`} />
                </div>

                <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
                  {[
                    { value: hasSpend ? fmtMoney(calc.newSpend, data._currency) : '—', label: 'Monthly spend', delta: calc.spendDelta, key: 'spend' },
                    { value: hasRevenue ? fmtMoney(calc.newRevenue, data._currency) : '—', label: 'Monthly revenue', delta: calc.revDelta, key: 'rev' },
                    { value: `${calc.efficiencyPct}%`, label: 'Efficiency index', delta: calc.effDelta, key: 'efficiency' },
                    { value: `${calc.scaleIndex}%`, label: 'Scale index', delta: calc.scaleDelta, key: 'scale' },
                  ].map((item) => (
                    <FlashCard key={item.key} value={item.value} label={item.label}>
                      <div className={cn('mt-1.5 text-xs font-medium', getDeltaColor(item.delta))}>
                        {getDeltaPrefix(item.delta)}{item.delta}% vs baseline
                      </div>
                    </FlashCard>
                  ))}
                </div>

                <div>
                  <div className="mb-1.5 flex justify-end gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><i className="h-[3px] w-2.5 rounded-sm bg-report-amber" />Spend</span>
                    <span className="inline-flex items-center gap-1.5"><i className="h-[3px] w-2.5 rounded-sm bg-report-green" />Revenue</span>
                    <span className="inline-flex items-center gap-1.5"><i className="h-[3px] w-2.5 rounded-sm bg-report-blue" />ROAS</span>
                  </div>
                  <MultiLine
                    xLabels={multipliers.map((m) => `${m}×`)}
                    series={[
                      { points: chartPoints.map((p) => p.spend), color: 'var(--report-amber)', axis: 'usd' },
                      { points: chartPoints.map((p) => p.revenue), color: 'var(--report-green)', axis: 'usd' },
                      { points: chartPoints.map((p) => p.roas), color: 'var(--report-blue)', axis: 'roas' },
                    ]}
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-dashed border-border pt-3.5 text-xs text-muted-foreground">
                  <span>Efficiency index: <strong className="text-foreground">{efficiencyIndex}%</strong> (current ROAS / baseline ROAS)</span>
                  <span>Projections are modelled on historical baselines — actual results will vary.</span>
                </div>
              </div>
            ) : (
              // No real revenue / ARPU / conversion source (RevenueCat absent) →
              // surface the real baseline facts and gate the projection rather
              // than modeling growth off fabricated benchmark numbers.
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2.5">
                  <FlashCard value={hasSpend ? fmtMoney(S0, data._currency) : '—'} label="Monthly spend" />
                  <FlashCard value={hasRevenue ? fmtMoney(R0, data._currency) : '—'} label="Monthly revenue" />
                </div>
                <div className="rounded-lg border border-dashed border-border p-6 text-center">
                  <p className="text-sm font-medium text-foreground">Revenue modeling unavailable</p>
                  <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                    Connect RevenueCat to project revenue, ROAS, and growth scenarios. ARPU,
                    conversion rate, and revenue can't be modeled without real subscription data.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </Section>

      {/* TAM/SAM/SOM */}
      <Section
        title="Market sizing (TAM / SAM / SOM)"
        hint="Total, serviceable and obtainable market for your category."
      >
        <Card>
          <CardContent className="px-5.5 py-1">
            <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-[1fr_auto]">
              {/* Detailed tier breakdown + penetration — left */}
              <div className="space-y-5">
                <div className="divide-y divide-border">
                  {tiers.map((m) => (
                    <div className="flex items-start gap-3.5 py-4.5 first:pt-0 last:pb-0" key={m.badge}>
                      <span
                        className={cn(
                          'mt-0.5 flex-none rounded-full px-3 py-1 font-mono text-[10px] font-bold tracking-[0.05em]',
                          TIER_TAGS[m.badge],
                        )}
                      >
                        {m.badge.toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="text-sm font-semibold text-foreground">{m.title}</div>
                        <div className="text-2xl font-bold tabular-nums text-foreground">{m.label}</div>
                        {m.desc && (
                          <div className="text-[13px] leading-relaxed text-foreground/80 text-pretty">{m.desc}</div>
                        )}
                        {m.sources.length > 0 && (
                          <div className="text-xs text-muted-foreground">Sources: {m.sources.join(', ')}</div>
                        )}
                        {m.current && (
                          <div className="text-xs text-muted-foreground">Current: {m.current}</div>
                        )}
                        {m.penetrationPath && (
                          <div className="text-xs text-muted-foreground">Growth path: {m.penetrationPath}</div>
                        )}
                        {m.target12m && (
                          <div className="text-xs text-muted-foreground">12-month target: {m.target12m}</div>
                        )}
                        {m.target3y && (
                          <div className="text-xs text-muted-foreground">3-year target: {m.target3y}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Penetration — under tiers */}
                <div className="rounded-[10px] border border-border bg-muted/30 px-4 py-3">
                  <div className="flex items-center gap-4">
                    <span className="shrink-0 text-sm text-muted-foreground">
                      Market penetration: <strong className="tabular-nums text-foreground">{String(currentPenetration)}%</strong>
                      <span className="ml-1 text-xs">({String(currentRevenue)} of {som.label})</span>
                    </span>
                    {/* Segmented progress bar */}
                    {/* Mobile: 60 segments */}
                    <div className="flex min-w-0 flex-1 gap-px lg:hidden">
                      {Array.from({ length: 60 }, (_, i) => (
                        <div
                          key={i}
                          className={cn(
                            'h-3.5 flex-1 rounded-[2px] transition-colors',
                            (i + 1) * (100 / 60) <= Number(currentPenetration) ? 'bg-secondary' : 'bg-muted'
                          )}
                        />
                      ))}
                    </div>
                    {/* Desktop: 160 segments */}
                    <div className="hidden min-w-0 flex-1 gap-px lg:flex">
                      {Array.from({ length: 160 }, (_, i) => (
                        <div
                          key={i}
                          className={cn(
                            'h-3.5 flex-1 rounded-[2px] transition-colors',
                            (i + 1) * (100 / 160) <= Number(currentPenetration) ? 'bg-secondary' : 'bg-muted'
                          )}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Concentric rings visualization — right, larger */}
              <div className="hidden items-center justify-center md:flex">
                <svg width="360" height="360" viewBox="0 0 360 360" className="shrink-0">
                  {/* TAM — outer ring, gray */}
                  <circle cx="180" cy="180" r="170" className="fill-muted stroke-border" strokeWidth="1.5" />
                  {/* SAM — middle ring, solid primary */}
                  <circle cx="180" cy="180" r="110" className="fill-primary" />
                  {/* SOM — inner circle, report blue (prototype's mkr__tag--som) */}
                  <circle cx="180" cy="180" r="56" fill="var(--report-blue)" />

                  {/* TAM label — top of outer ring */}
                  <text x="180" y="32" textAnchor="middle" className="fill-muted-foreground font-semibold" style={{ fontSize: 11 }}>TAM</text>
                  <text x="180" y="46" textAnchor="middle" className="fill-muted-foreground font-medium" style={{ fontSize: 12 }}>{tam.label}</text>

                  {/* SAM label — top of middle ring */}
                  <text x="180" y="88" textAnchor="middle" className="fill-primary-foreground font-semibold" style={{ fontSize: 11 }}>SAM</text>
                  <text x="180" y="102" textAnchor="middle" className="fill-primary-foreground font-medium" style={{ fontSize: 12 }}>{sam.label}</text>

                  {/* SOM label — center */}
                  <text x="180" y="174" textAnchor="middle" fill="#fff" className="font-semibold" style={{ fontSize: 11 }}>SOM</text>
                  <text x="180" y="188" textAnchor="middle" fill="#fff" className="font-medium" style={{ fontSize: 12 }}>{som.label}</text>
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* TAM Calculator */}
      <Section title="TAM calculator" hint="Adjust the parameters to model the market opportunity.">
        <Card>
          <CardContent className="px-5.5 py-1">
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-4">
                <CalculatorSlider label={isB2B ? 'Addressable Accounts (K)' : isEcommerce ? 'Addressable Customers (K)' : 'Global Users (M)'} value={activeTamUsers} min={tamUsersMin} max={tamUsersMax} step={Math.max(1, Math.round((tamUsersMax - tamUsersMin) / 100))} displayValue={`${activeTamUsers}${tamUnitLabel}`} onChange={setTamUsers} minLabel={`${tamUsersMin}${tamUnitLabel}`} maxLabel={`${tamUsersMax}${tamUnitLabel}`} />
                <CalculatorSlider label={isEcommerce ? 'Avg Customer Value ($/yr)' : 'Avg ARPU ($/yr)'} value={activeTamArpu} min={tamArpuMin} max={tamArpuMax} step={Math.max(1, Math.round((tamArpuMax - tamArpuMin) / 100))} displayValue={`$${activeTamArpu}`} onChange={setTamArpu} minLabel={`$${tamArpuMin}`} maxLabel={`$${tamArpuMax}`} />
                <CalculatorSlider label="Serviceable % of TAM" value={activeTamServiceable} min={1} max={50} step={0.5} displayValue={`${activeTamServiceable}%`} onChange={setTamServiceable} minLabel="1%" maxLabel="50%" />
                <CalculatorSlider label="Obtainable Share" value={activeTamObtainable} min={0.5} max={30} step={0.5} displayValue={`${activeTamObtainable}%`} onChange={setTamObtainable} minLabel="0.5%" maxLabel="30%" />
              </div>
              {(() => {
                const calcTam = activeTamUsers * tamUnit * activeTamArpu;
                const calcSam = calcTam * (activeTamServiceable / 100);
                const calcSom = calcSam * (activeTamObtainable / 100);

                const fmtSmart = (n: number) => {
                  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
                  if (n >= 1e6) return `$${Math.round(n / 1e6)}M`;
                  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
                  return `$${Math.round(n)}`;
                };

                const arrNum = R0 * 12; // computed from baseline annual revenue
                const penPct = calcSom > 0 ? (arrNum / calcSom) * 100 : 0;
                const penBarPct = Math.min(penPct, 100);
                const penLabel = calcSom > 0
                  ? `= ${penPct.toFixed(1)}% of SOM (${fmtSmart(calcSom)})`
                  : '—';

                const tamDelta = tam.value ? ((calcTam / tam.value - 1) * 100).toFixed(0) : '0';
                const samDelta = sam.value ? ((calcSam / sam.value - 1) * 100).toFixed(0) : '0';
                const somDelta = som.value ? ((calcSom / som.value - 1) * 100).toFixed(0) : '0';

                const items = [
                  { value: fmtSmart(calcTam), label: 'TAM', delta: tamDelta },
                  { value: fmtSmart(calcSam), label: 'SAM', delta: samDelta },
                  { value: fmtSmart(calcSom), label: 'SOM', delta: somDelta },
                ];

                return (
                  <>
                    <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
                      {items.map((item) => (
                        <FlashCard key={item.label} value={item.value} label={item.label}>
                          <div className={cn('mt-1.5 text-xs font-medium', getDeltaColor(item.delta))}>
                            {getDeltaPrefix(item.delta)}{item.delta}%
                          </div>
                        </FlashCard>
                      ))}
                      {/* Penetration card with ARR input */}
                      <FlashCard value={`${penPct.toFixed(1)}%`} label="Current penetration">
                        <div className="mt-1.5 flex w-full gap-px">
                          {Array.from({ length: 40 }, (_, i) => (
                            <div
                              key={i}
                              className={cn(
                                'h-2.5 flex-1 rounded-[1px]',
                                (i + 1) * (100 / 40) <= penBarPct ? 'bg-secondary' : 'bg-muted'
                              )}
                            />
                          ))}
                        </div>
                      </FlashCard>
                    </div>

                    {/* Your ARR (computed) + share label */}
                    <div className="flex items-center gap-3 rounded-[10px] border border-border bg-muted/30 px-4 py-3 text-[13px]">
                      <span className="shrink-0 text-muted-foreground">Your ARR</span>
                      <span className="font-semibold tabular-nums text-foreground">{fmtSmart(arrNum)}</span>
                      <span className="text-muted-foreground">{penLabel}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      </Section>
    </div>
  );
}
